/*
 * Hook: 获取 Kubernetes 工作负载数据
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import {
  PodStatus,
  DeploymentStatus,
  ServiceInfo,
  IngressInfo,
  EventInfo,
  ResourceOverview,
  ResourceMetrics,
} from '../types';

interface UseKubernetesWorkloadsResult {
  pods: PodStatus[];
  deployments: DeploymentStatus[];
  services: ServiceInfo[];
  ingresses: IngressInfo[];
  events: EventInfo[];
  overview: ResourceOverview;
  metrics: ResourceMetrics | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * 计算时间差
 */
function getAge(timestamp: string): string {
  const now = new Date();
  const created = new Date(timestamp);
  const diffMs = now.getTime() - created.getTime();
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

/**
 * 解析 Pod 状态
 */
function parsePodStatus(pod: any): PodStatus {
  const phase = pod.status?.phase || 'Unknown';
  const containerStatuses = pod.status?.containerStatuses || [];
  
  const readyContainers = containerStatuses.filter((c: any) => c.ready).length;
  const totalContainers = containerStatuses.length || pod.spec?.containers?.length || 0;
  
  const restarts = containerStatuses.reduce(
    (sum: number, c: any) => sum + (c.restartCount || 0),
    0
  );
  
  let status: PodStatus['status'] = 'Unknown';
  if (phase === 'Running' && readyContainers === totalContainers) {
    status = 'Running';
  } else if (phase === 'Pending') {
    status = 'Pending';
  } else if (phase === 'Failed' || phase === 'Error') {
    status = 'Failed';
  } else if (phase === 'Succeeded') {
    status = 'Succeeded';
  }
  
  // 从 spec 中获取原始镜像（带 tag），status 中可能只有 digest
  const specContainers = pod.spec?.containers || [];
  const specImageMap = new Map<string, string>();
  specContainers.forEach((sc: any) => {
    if (sc.name && sc.image) specImageMap.set(sc.name, sc.image);
  });

  return {
    name: pod.metadata.name,
    namespace: pod.metadata.namespace,
    status,
    ready: `${readyContainers}/${totalContainers}`,
    restarts,
    age: getAge(pod.metadata.creationTimestamp),
    node: pod.spec?.nodeName || '-',
    ip: pod.status?.podIP,
    containers: containerStatuses.map((c: any) => ({
      name: c.name,
      image: specImageMap.get(c.name) || c.image,
      ready: c.ready,
      restartCount: c.restartCount || 0,
      state: c.state?.running ? 'running' : c.state?.waiting ? 'waiting' : 'terminated',
      reason: c.state?.waiting?.reason || c.state?.terminated?.reason,
    })),
  };
}

/**
 * 解析 Deployment 状态
 */
function parseDeploymentStatus(deployment: any): DeploymentStatus {
  const spec = deployment.spec || {};
  const status = deployment.status || {};
  
  // 获取镜像
  const containers = spec.template?.spec?.containers || [];
  const image = containers[0]?.image || '-';
  
  return {
    name: deployment.metadata.name,
    namespace: deployment.metadata.namespace,
    replicas: spec.replicas || 0,
    readyReplicas: status.readyReplicas || 0,
    availableReplicas: status.availableReplicas || 0,
    updatedReplicas: status.updatedReplicas || 0,
    strategy: spec.strategy?.type || 'RollingUpdate',
    image,
    age: getAge(deployment.metadata.creationTimestamp),
    conditions: (status.conditions || []).map((c: any) => ({
      type: c.type,
      status: c.status,
      reason: c.reason,
      message: c.message,
      lastUpdateTime: c.lastUpdateTime,
    })),
  };
}

/**
 * 解析 Service 信息
 */
function parseServiceInfo(service: any): ServiceInfo {
  const spec = service.spec || {};
  
  return {
    name: service.metadata.name,
    namespace: service.metadata.namespace,
    type: spec.type || 'ClusterIP',
    clusterIP: spec.clusterIP || '-',
    externalIP: spec.externalIPs?.[0] || service.status?.loadBalancer?.ingress?.[0]?.ip,
    ports: (spec.ports || []).map((p: any) => ({
      name: p.name,
      port: p.port,
      targetPort: p.targetPort,
      protocol: p.protocol || 'TCP',
      nodePort: p.nodePort,
    })),
    age: getAge(service.metadata.creationTimestamp),
  };
}

/**
 * 解析 Ingress 信息
 */
function parseIngressInfo(ingress: any): IngressInfo {
  const spec = ingress.spec || {};
  const status = ingress.status || {};
  
  const hosts = (spec.rules || []).map((r: any) => r.host).filter(Boolean);
  const address = status.loadBalancer?.ingress?.[0]?.ip || 
                  status.loadBalancer?.ingress?.[0]?.hostname;
  
  return {
    name: ingress.metadata.name,
    namespace: ingress.metadata.namespace,
    hosts,
    address,
    age: getAge(ingress.metadata.creationTimestamp),
  };
}

/**
 * 解析 Event 信息
 */
function parseEventInfo(event: any): EventInfo {
  return {
    type: event.type || 'Normal',
    reason: event.reason || '-',
    message: event.message || '-',
    involvedObject: {
      kind: event.involvedObject?.kind || '-',
      name: event.involvedObject?.name || '-',
    },
    count: event.count || 1,
    firstTimestamp: event.firstTimestamp || event.metadata?.creationTimestamp,
    lastTimestamp: event.lastTimestamp || event.metadata?.creationTimestamp,
    age: getAge(event.lastTimestamp || event.metadata?.creationTimestamp),
  };
}

/**
 * 获取 Kubernetes 工作负载数据
 */
export function useKubernetesWorkloads(
  clusterName: string,
  namespace: string,
  labelSelector?: string
): UseKubernetesWorkloadsResult {
  const kubernetesApi = useApi(kubernetesApiRef);
  
  const [pods, setPods] = useState<PodStatus[]>([]);
  const [deployments, setDeployments] = useState<DeploymentStatus[]>([]);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [ingresses, setIngresses] = useState<IngressInfo[]>([]);
  const [events, setEvents] = useState<EventInfo[]>([]);
  const [metrics, setMetrics] = useState<ResourceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const fetchData = useCallback(async () => {
    if (!clusterName || !namespace) {
      setError(new Error('Missing cluster name or namespace'));
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const labelQuery = labelSelector ? `?labelSelector=${encodeURIComponent(labelSelector)}` : '';
      
      // 并行获取所有资源
      const [podsRes, deploymentsRes, servicesRes, ingressesRes, eventsRes] = await Promise.all([
        kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/pods${labelQuery}`,
        }),
        kubernetesApi.proxy({
          clusterName,
          path: `/apis/apps/v1/namespaces/${namespace}/deployments${labelQuery}`,
        }),
        kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/services`,
        }),
        kubernetesApi.proxy({
          clusterName,
          path: `/apis/networking.k8s.io/v1/namespaces/${namespace}/ingresses`,
        }),
        kubernetesApi.proxy({
          clusterName,
          path: `/api/v1/namespaces/${namespace}/events?limit=50`,
        }),
      ]);
      
      // 解析响应
      const podsData = await podsRes.json();
      const deploymentsData = await deploymentsRes.json();
      const servicesData = await servicesRes.json();
      const ingressesData = await ingressesRes.json();
      const eventsData = await eventsRes.json();
      
      // 转换数据
      const parsedPods = (podsData.items || []).map(parsePodStatus);
      const parsedDeployments = (deploymentsData.items || []).map(parseDeploymentStatus);
      const parsedServices = (servicesData.items || []).map(parseServiceInfo);
      const parsedIngresses = (ingressesData.items || []).map(parseIngressInfo);
      const parsedEvents = (eventsData.items || [])
        .map(parseEventInfo)
        .sort((a: EventInfo, b: EventInfo) => 
          new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime()
        )
        .slice(0, 20);
      
      setPods(parsedPods);
      setDeployments(parsedDeployments);
      setServices(parsedServices);
      setIngresses(parsedIngresses);
      setEvents(parsedEvents);
      
      // 计算资源使用指标（简化版，实际需要 metrics-server）
      const totalCpuRequests = parsedPods.reduce((sum: number, _pod: PodStatus) => sum + 100, 0); // 模拟数据
      const totalMemRequests = parsedPods.reduce((sum: number, _pod: PodStatus) => sum + 128 * 1024 * 1024, 0);
      
      setMetrics({
        cpu: {
          usage: totalCpuRequests * 0.65,
          requests: totalCpuRequests,
          limits: totalCpuRequests * 2,
          percentage: 65,
        },
        memory: {
          usage: totalMemRequests * 0.45,
          requests: totalMemRequests,
          limits: totalMemRequests * 2,
          percentage: 45,
        },
      });
      
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi, clusterName, namespace, labelSelector, refreshKey]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  // 计算概览统计
  const overview: ResourceOverview = {
    pods: {
      total: pods.length,
      running: pods.filter(p => p.status === 'Running').length,
      pending: pods.filter(p => p.status === 'Pending').length,
      failed: pods.filter(p => p.status === 'Failed').length,
    },
    services: services.length,
    ingresses: ingresses.length,
    configMaps: 0, // TODO: 获取 ConfigMaps
    secrets: 0, // TODO: 获取 Secrets
  };
  
  return {
    pods,
    deployments,
    services,
    ingresses,
    events,
    overview,
    metrics,
    loading,
    error,
    refresh,
  };
}

